import { RefObject, useEffect } from 'react';
import { clearSharedPlaybackCache, LibraryDetail, isSharedPlayback, revalidateLibraryAudio } from '../api/libraryPlayback';

/** Shared bytes are memory-only and must stop on authorization loss or offline. */
export function useSharedPlaybackAuthority(detail: LibraryDetail | null, audio: RefObject<HTMLAudioElement | null>, unavailable: () => void): void {
  useEffect(() => {
    if (!detail || !isSharedPlayback(detail.render_id)) return;
    const controller = new AbortController();
    let checking = false;
    const stop = () => { audio.current?.pause(); clearSharedPlaybackCache(); unavailable(); };
    const check = async () => {
      if (checking || controller.signal.aborted) return;
      checking = true;
      try {
        const valid = await revalidateLibraryAudio(detail, controller.signal);
        if (!controller.signal.aborted && !valid) stop();
      } catch { if (!controller.signal.aborted) stop(); }
      finally { checking = false; }
    };
    const focus = () => { audio.current?.pause(); void check(); };
    const timer = window.setInterval(() => void check(), 10_000);
    window.addEventListener('focus', focus);
    window.addEventListener('pageshow', focus);
    const visibility = () => { void check(); };
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('offline', stop);
    window.addEventListener('luma-access-invalidated', stop);
    return () => {
      controller.abort(); window.clearInterval(timer);
      window.removeEventListener('focus', focus);
      window.removeEventListener('pageshow', focus);
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('offline', stop);
      window.removeEventListener('luma-access-invalidated', stop);
    };
  }, [detail, audio, unavailable]);
}
